import React, { useEffect, useState } from 'react';
import { Routes, Route, Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { OktaAuth, toRelativeUrl } from '@okta/okta-auth-js';
import { Security, LoginCallback, useOktaAuth } from '@okta/okta-react';
import Header from './components/Header';
import Home from './pages/Home';
import Contact from './pages/Contact';
import { getOktaConfig } from './okta/config';
import logoTiffany from './assets/logo_tiffany.png';
import logoGeorge from './assets/logo_george.png';

const ScrollToTop: React.FC = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

// Auth guard: everything inside the agent store requires a valid Okta session.
// If not authenticated, fire signInWithRedirect immediately and render nothing
// while the redirect happens (same pattern as tif-tap-conversational-frontend).
const ProtectedShell: React.FC = () => {
  const { authState, oktaAuth } = useOktaAuth();

  useEffect(() => {
    if (!authState) return;
    if (!authState.isAuthenticated) {
      void oktaAuth.signInWithRedirect();
    }
  }, [authState, oktaAuth]);

  if (!authState || !authState.isAuthenticated) return null;

  return (
    <div className="min-h-screen bg-white text-black flex flex-col relative overflow-hidden">
      <div className="fixed top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[600px] bg-gradient-to-b from-[#81D8D0]/20 to-transparent rounded-[100%] blur-[100px] pointer-events-none z-0" />
      <Header />
      <main className="flex-grow relative z-10 pt-24">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      <footer className="relative z-10 bg-[#81D8D0] mt-8 md:mt-12 overflow-hidden">
        <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-white/20 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-32 -right-32 w-96 h-96 rounded-full bg-white/10 blur-3xl pointer-events-none" />
        <div className="relative max-w-6xl mx-auto px-6 pt-8 md:pt-10 pb-14 md:pb-16">
          <div className="flex flex-col items-center text-center">
            <div className="flex items-center justify-center gap-4 mb-5">
              <img
                src={logoTiffany}
                alt="Tiffany & Co."
                className="h-4 md:h-5 w-auto object-contain"
              />
              <div className="h-6 w-px bg-black/20" />
              <img src={logoGeorge} alt="George" className="h-10 md:h-12 w-auto object-contain" />
            </div>
            <p className="text-sm md:text-base font-medium text-black/80 max-w-md">
              Tiffany &amp; Co. George Agent Store
            </p>
            <p className="text-xs text-black/60 mt-1">Powered by Agentic Team</p>
            <nav className="flex items-center justify-center gap-6 mt-6">
              <Link
                to="/"
                className="text-sm font-medium text-black hover:opacity-60 transition-opacity"
              >
                Agent Store
              </Link>
              <span className="w-1 h-1 rounded-full bg-black/30" />
              <Link
                to="/contact"
                className="text-sm font-medium text-black hover:opacity-60 transition-opacity"
              >
                Contact
              </Link>
            </nav>
          </div>
          <div className="my-8 h-px w-full bg-black/10" />
          <div className="text-center text-xs text-black/70 font-medium tracking-wide">
            &copy; {new Date().getFullYear()} Tiffany &amp; Co. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

const useRestoreOriginalUri = (): ((
  _oktaAuth: OktaAuth,
  originalUri: string | undefined
) => void) => {
  const navigate = useNavigate();
  return (_oktaAuth, originalUri) => {
    navigate(toRelativeUrl(originalUri ?? '/', window.location.origin));
  };
};

const App: React.FC = () => {
  const [oktaAuth, setOktaAuth] = useState<OktaAuth | null>(null);
  const restoreOriginalUri = useRestoreOriginalUri();

  useEffect(() => {
    getOktaConfig()
      .then(({ oidc }) => setOktaAuth(new OktaAuth(oidc)))
      .catch((error: unknown) => {
        console.error('Error loading Okta config:', error);
      });
  }, []);

  if (!oktaAuth) return null;

  return (
    <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
      <ScrollToTop />
      <Routes>
        <Route path="/login/openid-redirect-uri" element={<LoginCallback />} />
        <Route path="*" element={<ProtectedShell />} />
      </Routes>
    </Security>
  );
};

export default App;
